import { useEffect, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { Download, PhoneCall, LifeBuoy, GraduationCap, CreditCard, Video, CalendarClock, Paperclip, Play } from "lucide-react";
import { toEmbedUrl, isLocalMedia, mediaSrc } from "../../lib/embed";
import { toast } from "sonner";
import api from "../../lib/api";
import { FadeUp, SectionTag, StatusPill } from "../../components/site/Reveal";

const TYPE_COLORS = {
  Spreadsheet: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
  Scripts: "text-sky-300 border-sky-400/30 bg-sky-400/10",
  Calculator: "text-amber-300 border-amber-400/30 bg-amber-400/10",
  Checklist: "text-violet-300 border-violet-400/30 bg-violet-400/10",
  Document: "text-safety border-safety/40 bg-safety/10",
};

export function Templates() {
  const { content } = useOutletContext();
  const [filter, setFilter] = useState("all");
  const types = ["all", ...new Set(content.templates.map((t) => t.type))];
  const list = filter === "all" ? content.templates : content.templates.filter((t) => t.type === filter);

  const download = (t) => {
    const blob = new Blob([t.content], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${t.name.replace(/\s+/g, "-")}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Downloaded ${t.name}`);
  };

  return (
    <div data-testid="hub-templates" className="max-w-5xl">
      <FadeUp>
        <SectionTag dark>The Vault // Yours to Keep</SectionTag>
        <h1 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl mt-4">Template Vault</h1>
        <p className="text-slate-400 mt-3 max-w-2xl">Every artifact from the program. Download, adapt, install.</p>
      </FadeUp>
      <FadeUp delay={0.1} className="mt-6 flex flex-wrap gap-2">
        {types.map((t) => (
          <button key={t} data-testid={`template-filter-${t.toLowerCase()}`} onClick={() => setFilter(t)}
            className={`font-mono text-[11px] uppercase tracking-wider px-3.5 py-2 border transition-colors ${
              filter === t ? "border-safety text-safety bg-safety/10" : "border-white/15 text-slate-400 hover:border-white/40"
            }`}>
            {t}
          </button>
        ))}
      </FadeUp>
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
        {list.map((t, i) => (
          <FadeUp key={t.id} delay={i * 0.03} className="h-full">
            <div className="bg-charcoal p-6 h-full flex flex-col" data-testid={`template-card-${t.id}`}>
              <div className="flex justify-between items-start">
                <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${TYPE_COLORS[t.type]}`}>{t.type}</span>
                <span className="font-mono text-[10px] text-slate-600">{t.module.toUpperCase()}</span>
              </div>
              <h3 className="font-display font-bold text-white mt-4">{t.name}</h3>
              <p className="text-slate-400 text-sm mt-2 leading-relaxed flex-1">{t.description}</p>
              <button onClick={() => download(t)} data-testid={`template-download-${t.id}`}
                className="mt-5 flex items-center gap-2 text-safety hover:text-white text-sm font-semibold transition-colors">
                <Download size={15} /> Download
              </button>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  );
}

export function ImplementationDashboard() {
  const { content, progress, toggle } = useOutletContext();
  const done = (mid, id) => progress.completed.includes(`${mid}:${id}`);
  return (
    <div data-testid="hub-implementation-dashboard" className="max-w-5xl">
      <FadeUp>
        <SectionTag dark>Build Board // All Tasks</SectionTag>
        <h1 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl mt-4">Implementation Dashboard</h1>
        <p className="text-slate-400 mt-3 max-w-2xl">Every build task across the seven modules. Check them off as you install.</p>
      </FadeUp>
      <div className="mt-8 grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {content.modules.map((m, i) => {
          const pm = progress.per_module[m.id];
          return (
            <FadeUp key={m.id} delay={i * 0.04}>
              <div className="bg-charcoal border border-white/10 p-5" data-testid={`board-column-${m.id}`}>
                <div className="flex justify-between items-center mb-4">
                  <p className="font-mono text-[11px] tracking-[0.18em] text-safety">[{m.code}] {m.title}</p>
                  <p className="font-mono text-[10px] text-slate-500">{pm?.done || 0}/{pm?.total}</p>
                </div>
                <div className="space-y-2">
                  {m.tasks.map((t) => (
                    <button key={t.id} data-testid={`board-task-${m.id}-${t.id}`} onClick={() => toggle(`${m.id}:${t.id}`, !done(m.id, t.id))}
                      className={`w-full text-left px-3 py-2.5 border text-xs leading-relaxed transition-colors ${
                        done(m.id, t.id)
                          ? "border-emerald-500/30 bg-emerald-500/5 text-slate-500 line-through"
                          : "border-white/10 bg-navy text-slate-300 hover:border-safety/40"
                      }`}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            </FadeUp>
          );
        })}
      </div>
    </div>
  );
}

export function CoachingCalls() {
  const { content } = useOutletContext();
  const calls = content.coaching_calls || [];
  const replays = content.replays || [];
  const fmt = (iso) => new Date(iso).toLocaleString(undefined, {
    weekday: "long", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });

  return (
    <div data-testid="hub-coaching-calls" className="max-w-4xl">
      <FadeUp>
        <SectionTag dark>Live // Weekly</SectionTag>
        <h1 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl mt-4">Coaching Calls</h1>
        <p className="text-slate-400 mt-3 leading-relaxed">
          Two live sessions every week. Bring your actual bottleneck — leave with the next action.
        </p>
      </FadeUp>

      <FadeUp delay={0.1} className="mt-8">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">[UPCOMING SESSIONS]</p>
        <div className="space-y-px bg-white/10 border border-white/10">
          {calls.length === 0 && <p className="bg-charcoal p-6 text-slate-500 text-sm">Next sessions are being scheduled — check back shortly.</p>}
          {calls.map((c, i) => (
            <div key={c.call_id} className="bg-charcoal p-6 flex flex-wrap items-center gap-5" data-testid={`call-card-${c.call_id.slice(0, 8)}`}>
              <div className="w-12 h-12 bg-safety/10 border border-safety/30 grid place-items-center shrink-0">
                <CalendarClock size={20} className="text-safety" />
              </div>
              <div className="flex-1 min-w-[200px]">
                <h3 className="font-display font-bold text-white">{c.title}</h3>
                <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500 mt-1">{fmt(c.starts_at)}</p>
                {c.note && <p className="text-slate-400 text-xs mt-1.5">{c.note}</p>}
              </div>
              {c.join_url ? (
                <a href={c.join_url} target="_blank" rel="noopener noreferrer" data-testid={`call-join-${c.call_id.slice(0, 8)}`}
                  className="bg-safety hover:bg-safety-hover text-white text-sm font-semibold px-5 py-2.5 transition-colors">
                  Join call
                </a>
              ) : (
                <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500 border border-white/15 px-3 py-2">
                  Link emailed before start
                </span>
              )}
            </div>
          ))}
        </div>
      </FadeUp>

      <FadeUp delay={0.2} className="mt-10">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">[REPLAY ARCHIVE]</p>
        {replays.length === 0 ? (
          <div className="bg-charcoal border border-white/10 p-8 text-center" data-testid="replays-empty">
            <Video size={32} className="text-slate-600 mx-auto" />
            <p className="text-slate-400 text-sm mt-3">Replays are posted here after each call. Miss one? Catch up on your own schedule.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {replays.map((r, i) => {
              const embed = toEmbedUrl(r.video_url);
              return (
                <FadeUp key={r.replay_id} delay={i * 0.05} className="h-full">
                  <div className="bg-charcoal border border-white/10 h-full flex flex-col" data-testid={`replay-card-${r.replay_id.slice(0, 8)}`}>
                    {embed && embed !== r.video_url ? (
                      <div className="aspect-video bg-black">
                        <iframe src={embed} title={r.title} className="w-full h-full" allowFullScreen
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
                      </div>
                    ) : (
                      <a href={r.video_url} target="_blank" rel="noopener noreferrer"
                        className="aspect-video bg-navy grid place-items-center border-b border-white/10 group">
                        <div className="w-12 h-12 bg-safety/15 border border-safety/40 grid place-items-center group-hover:bg-safety transition-colors">
                          <Video size={18} className="text-safety group-hover:text-white" />
                        </div>
                      </a>
                    )}
                    <div className="p-5 flex-1">
                      <h3 className="font-display font-bold text-white text-sm">{r.title}</h3>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-1">
                        Recorded {new Date(r.recorded_at).toLocaleDateString()}
                      </p>
                      {r.summary && <p className="text-slate-400 text-xs mt-2 leading-relaxed">{r.summary}</p>}
                    </div>
                  </div>
                </FadeUp>
              );
            })}
          </div>
        )}
      </FadeUp>
    </div>
  );
}

export function HubSection({ sectionKey }) {
  const { content } = useOutletContext();
  const s = (content.sections || []).find((x) => x.key === sectionKey);
  if (!s) return <p className="text-slate-400">Section unavailable.</p>;
  const embed = toEmbedUrl(s.video_url);
  const media = isLocalMedia(s.video_url);

  return (
    <div data-testid={`hub-section-${sectionKey}`} className="max-w-3xl">
      <FadeUp>
        <SectionTag dark>Member Resource // Video</SectionTag>
        <h1 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl mt-4">{s.title}</h1>
        <p className="text-slate-400 mt-3 max-w-2xl leading-relaxed">{s.blurb}</p>
      </FadeUp>
      <FadeUp delay={0.1} className="mt-7">
        {!s.video_url ? (
          <div className="aspect-video bg-charcoal border border-white/10 grid place-items-center blueprint-grid" data-testid={`section-video-placeholder-${sectionKey}`}>
            <div className="text-center px-6">
              <div className="w-14 h-14 mx-auto bg-safety/15 border border-safety/40 grid place-items-center">
                <Play size={22} className="text-safety ml-0.5" />
              </div>
              <p className="text-slate-300 font-medium mt-4 text-sm">Video walkthrough coming soon</p>
              <p className="text-slate-500 text-xs mt-1">Your coach publishes the session recording here.</p>
            </div>
          </div>
        ) : media ? (
          <video controls playsInline preload="metadata" src={mediaSrc(s.video_url)}
            className="w-full aspect-video bg-black border border-white/10" data-testid={`section-video-${sectionKey}`} />
        ) : (
          <div className="aspect-video bg-black border border-white/10" data-testid={`section-video-${sectionKey}`}>
            <iframe src={embed} title={s.title} className="w-full h-full" allowFullScreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
          </div>
        )}
      </FadeUp>
    </div>
  );
}

export function AskForHelp() {
  const { content } = useOutletContext();
  const [params] = useSearchParams();
  const preModule = params.get("module") || "";
  const preLesson = params.get("lesson") || "";
  const preStuck = params.get("stuck") || "";
  const [form, setForm] = useState({
    subject: preStuck ? `Stuck: ${preStuck}` : "",
    message: "", module: preModule, lesson: preLesson, stuck_point: preStuck, tried: "",
  });
  const [shot, setShot] = useState(null);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [myTickets, setMyTickets] = useState([]);
  const [followUps, setFollowUps] = useState({});
  const [replyBusy, setReplyBusy] = useState("");

  const loadMine = async () => {
    try {
      const { data } = await api.get("/hub/help");
      setMyTickets(data.tickets);
    } catch { /* list is best-effort */ }
  };

  useEffect(() => { loadMine(); }, []);
  useEffect(() => { if (sent) loadMine(); }, [sent]);

  const sendFollowUp = async (ticketId) => {
    const body = (followUps[ticketId] || "").trim();
    if (body.length < 2) return;
    setReplyBusy(ticketId);
    try {
      await api.post(`/hub/help/${ticketId}/reply`, { body });
      toast.success("Follow-up sent.");
      setFollowUps({ ...followUps, [ticketId]: "" });
      loadMine();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send — try again.");
    } finally {
      setReplyBusy("");
    }
  };

  const selModule = content.modules.find((m) => m.id === form.module);
  const selLesson = selModule?.lessons.find((l) => l.id === form.lesson);
  const stuckOptions = selLesson?.stuck_points || [];

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setShot(null); return; }
    if (!f.type.startsWith("image/")) { toast.error("Screenshot must be an image file"); e.target.value = ""; return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Screenshot must be under 5 MB"); e.target.value = ""; return; }
    setShot(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/hub/help", {
        subject: form.subject, message: form.message, module: form.module,
        lesson: form.lesson, stuck_point: form.stuck_point, tried: form.tried,
      });
      if (shot) {
        const fd = new FormData();
        fd.append("file", shot);
        await api.post(`/hub/help/${data.ticket_id}/screenshot`, fd);
      }
      setSent(true);
      toast.success("Request received — we'll get back to you.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send — try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="hub-ask-help" className="max-w-2xl">
      <FadeUp>
        <SectionTag dark>SOS // Priority Support</SectionTag>
        <h1 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl mt-4">Ask for Help</h1>
        <p className="text-slate-400 mt-3">Stuck on a build? Hit a wall with a tool? This goes straight to your coach.</p>
      </FadeUp>
      {sent ? (
        <FadeUp className="mt-8 bg-charcoal border border-emerald-500/30 p-10 text-center" data-testid="help-success">
          <LifeBuoy size={44} className="text-emerald-400 mx-auto" />
          <h2 className="font-display font-bold text-white text-2xl mt-5">Request logged.</h2>
          <p className="text-slate-400 text-sm mt-2">Expect a reply within 1 business day — usually faster.</p>
        </FadeUp>
      ) : (
        <FadeUp delay={0.1}>
          <form onSubmit={submit} className="mt-8 bg-charcoal border border-white/10 p-6 sm:p-8 space-y-5" data-testid="help-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-slate-300 text-sm font-medium mb-2">Module</label>
                <select value={form.module} data-testid="help-module-select"
                  onChange={(e) => setForm({ ...form, module: e.target.value, lesson: "", stuck_point: "" })}
                  className="w-full bg-navy border border-white/15 text-white px-4 py-3 text-sm focus:border-safety outline-none">
                  <option value="">General</option>
                  {content.modules.map((m) => <option key={m.id} value={m.id}>{m.code} — {m.title}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-slate-300 text-sm font-medium mb-2">Lesson</label>
                <select value={form.lesson} data-testid="help-lesson-select" disabled={!selModule}
                  onChange={(e) => setForm({ ...form, lesson: e.target.value, stuck_point: "" })}
                  className="w-full bg-navy border border-white/15 text-white px-4 py-3 text-sm focus:border-safety outline-none disabled:opacity-40">
                  <option value="">{selModule ? "Select a lesson" : "Pick a module first"}</option>
                  {(selModule?.lessons || []).map((l) => <option key={l.id} value={l.id}>{l.title}</option>)}
                </select>
              </div>
            </div>
            {selLesson && stuckOptions.length > 0 && (
              <div>
                <label className="block text-slate-300 text-sm font-medium mb-2">Where are you stuck?</label>
                <select value={form.stuck_point} data-testid="help-stuck-select"
                  onChange={(e) => setForm({ ...form, stuck_point: e.target.value })}
                  className="w-full bg-navy border border-white/15 text-white px-4 py-3 text-sm focus:border-safety outline-none">
                  <option value="">Choose a common obstacle…</option>
                  {stuckOptions.map((sp) => <option key={sp.id} value={sp.label}>{sp.label}</option>)}
                  <option value="Something else">Something else</option>
                </select>
              </div>
            )}
            <div>
              <label className="block text-slate-300 text-sm font-medium mb-2">Subject</label>
              <input required minLength={3} data-testid="help-subject-input" value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="w-full bg-navy border border-white/15 text-white px-4 py-3 text-sm focus:border-safety outline-none" />
            </div>
            <div>
              <label className="block text-slate-300 text-sm font-medium mb-2">What's blocking you?</label>
              <textarea required minLength={10} rows={4} data-testid="help-message-input" value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full bg-navy border border-white/15 text-white px-4 py-3 text-sm focus:border-safety outline-none resize-none" />
            </div>
            <div>
              <label className="block text-slate-300 text-sm font-medium mb-2">What have you tried so far? (optional)</label>
              <textarea rows={3} data-testid="help-tried-input" value={form.tried}
                onChange={(e) => setForm({ ...form, tried: e.target.value })}
                placeholder="Steps you already attempted, tools you checked…"
                className="w-full bg-navy border border-white/15 text-white px-4 py-3 text-sm focus:border-safety outline-none resize-none placeholder:text-slate-600" />
            </div>
            <div>
              <label className="block text-slate-300 text-sm font-medium mb-2">Screenshot (optional)</label>
              <label className="flex items-center gap-3 bg-navy border border-dashed border-white/20 hover:border-safety/50 px-4 py-3.5 text-sm text-slate-400 cursor-pointer transition-colors"
                data-testid="help-screenshot-drop">
                <Paperclip size={15} className="text-safety shrink-0" />
                {shot ? <span className="text-white">{shot.name}</span> : "Attach an image — PNG, JPG, WEBP or GIF, max 5 MB"}
                <input type="file" accept="image/*" onChange={onFile} data-testid="help-screenshot-input" className="hidden" />
              </label>
            </div>
            <p className="text-slate-500 text-xs leading-relaxed" data-testid="help-disclaimer">
              Guidance here is educational — you remain responsible for your own business decisions. Verify tax, legal, insurance, or financial choices with a qualified professional.
            </p>
            <button type="submit" disabled={busy} data-testid="help-submit-btn"
              className="bg-safety hover:bg-safety-hover disabled:opacity-50 text-white font-semibold px-8 py-3.5 transition-colors">
              {busy ? "Sending…" : "Send request"}
            </button>
          </form>
        </FadeUp>
      )}

      <FadeUp delay={0.15} className="mt-10">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">[YOUR REQUESTS]</p>
        {myTickets.length === 0 ? (
          <p className="text-slate-500 text-sm" data-testid="my-tickets-empty">No requests yet — your coach conversation lives here once you send one.</p>
        ) : (
          <div className="space-y-px border border-white/10" data-testid="my-tickets">
            {myTickets.map((t) => {
              const tid = t.ticket_id.slice(0, 8);
              const answered = t.status === "Answered";
              return (
                <div key={t.ticket_id} className="bg-charcoal p-5" data-testid={`my-ticket-${tid}`}>
                  <div className="flex justify-between gap-4 items-center">
                    <p className="text-white font-medium">{t.subject}</p>
                    <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${
                      answered ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" : "text-amber-300 border-amber-400/30 bg-amber-400/10"
                    }`} data-testid={`my-ticket-status-${tid}`}>{t.status}</span>
                  </div>
                  <p className="font-mono text-[10px] text-slate-500 mt-1">{t.created_at.slice(0, 10)}</p>
                  <p className="text-slate-400 text-sm mt-2 leading-relaxed">{t.message}</p>
                  {(t.replies || []).length > 0 && (
                    <div className="mt-3 space-y-2.5 border-l-2 border-white/10 pl-3" data-testid={`my-ticket-replies-${tid}`}>
                      {t.replies.map((r) => (
                        <div key={r.reply_id}>
                          <p className={`font-mono text-[10px] uppercase tracking-widest ${r.author_role === "admin" ? "text-safety" : "text-slate-500"}`}>
                            {r.author_role === "admin" ? "Coach" : "You"} — {r.created_at.slice(0, 10)}
                          </p>
                          <p className="text-slate-300 text-sm mt-1 leading-relaxed">{r.body}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 flex gap-2">
                    <input value={followUps[t.ticket_id] || ""} data-testid={`member-reply-input-${tid}`}
                      onChange={(e) => setFollowUps({ ...followUps, [t.ticket_id]: e.target.value })}
                      placeholder="Send a follow-up…"
                      className="flex-1 bg-navy border border-white/15 text-white px-3 py-2 text-sm focus:border-safety outline-none placeholder:text-slate-600" />
                    <button onClick={() => sendFollowUp(t.ticket_id)} disabled={replyBusy === t.ticket_id}
                      data-testid={`member-reply-send-${tid}`}
                      className="bg-safety hover:bg-safety-hover disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 transition-colors">
                      {replyBusy === t.ticket_id ? "…" : "Send"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </FadeUp>
    </div>
  );
}

export function Account() {
  const { user } = useOutletContext();
  const [billing, setBilling] = useState(null);
  useEffect(() => {
    api.get("/hub/billing").then((r) => setBilling(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="hub-account" className="max-w-3xl">
      <FadeUp>
        <SectionTag dark>Account // Billing</SectionTag>
        <h1 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl mt-4">Account & Billing</h1>
      </FadeUp>
      <FadeUp delay={0.1} className="mt-8 bg-charcoal border border-white/10 p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-6">
          <CreditCard size={20} className="text-safety" />
          <h2 className="font-display font-bold text-white text-xl">Membership</h2>
        </div>
        <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
          <div><dt className="text-slate-500 font-mono text-[10px] uppercase tracking-widest">Email</dt><dd className="text-white mt-1">{user.email}</dd></div>
          <div><dt className="text-slate-500 font-mono text-[10px] uppercase tracking-widest">Program</dt><dd className="text-white mt-1">The Back Office Blueprint</dd></div>
          <div><dt className="text-slate-500 font-mono text-[10px] uppercase tracking-widest">Status</dt>
            <dd className="mt-1.5"><StatusPill status={billing?.application_status || "Enrolled"} /></dd></div>
          <div><dt className="text-slate-500 font-mono text-[10px] uppercase tracking-widest">Enrolled</dt>
            <dd className="text-white mt-1">{billing?.enrolled_at ? new Date(billing.enrolled_at).toLocaleDateString() : "—"}</dd></div>
        </dl>
      </FadeUp>
      <FadeUp delay={0.2} className="mt-6 bg-charcoal border border-white/10 p-6 sm:p-8">
        <h2 className="font-display font-bold text-white text-xl mb-5">Payment history</h2>
        {!billing || billing.transactions.length === 0 ? (
          <p className="text-slate-500 text-sm">No payments on record.</p>
        ) : (
          <div className="space-y-px">
            {billing.transactions.map((t) => (
              <div key={t.session_id} data-testid={`billing-txn-${t.session_id.slice(-6)}`}
                className="bg-navy border border-white/10 px-5 py-4 flex justify-between items-center text-sm">
                <div>
                  <p className="text-white font-medium">Blueprint enrollment</p>
                  <p className="font-mono text-[10px] text-slate-500 mt-0.5">{new Date(t.created_at).toLocaleDateString()} — {t.session_id.slice(0, 18)}…</p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-white">${(t.amount / 100).toFixed(2)}</p>
                  <p className={`font-mono text-[10px] uppercase tracking-widest ${t.payment_status === "paid" ? "text-emerald-400" : "text-amber-300"}`}>
                    {t.payment_status}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="text-slate-500 text-xs mt-5 leading-relaxed">
          Refunds are covered by the 14-day fit guarantee — see the Refund Policy or message us from the contact page.
        </p>
      </FadeUp>
    </div>
  );
}

export function Graduation() {
  const { progress } = useOutletContext();
  const pct = progress.total_items ? Math.round((progress.completed_count / progress.total_items) * 100) : 0;
  return (
    <div data-testid="hub-graduation" className="max-w-3xl">
      <FadeUp>
        <SectionTag dark>After the Blueprint // What Next</SectionTag>
        <h1 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl mt-4">Graduation / Next Steps</h1>
      </FadeUp>
      <FadeUp delay={0.1} className="mt-8 bg-charcoal border border-white/10 p-6 sm:p-8 flex items-center gap-5">
        <GraduationCap size={36} className={pct === 100 ? "text-safety" : "text-slate-600"} />
        <div>
          <p className="font-display font-bold text-white text-lg">
            {pct === 100 ? "Blueprint complete. You're operating on systems now." : `You're at ${pct}% — finish the build to graduate.`}
          </p>
          <p className="text-slate-400 text-sm mt-1">
            {pct === 100
              ? "Your back office no longer depends on memory and late nights."
              : "Every checked item is a system that stays when the program ends."}
          </p>
        </div>
      </FadeUp>
      <FadeUp delay={0.2} className="mt-6 space-y-px bg-white/10 border border-white/10">
        {[
          { t: "Keep the weekly routines", d: "Lead review Monday, money routine Friday, 10-minute record tidy-up. The systems only work if the routines run." },
          { t: "Prepare for your first hire", d: "Your checklists, SOPs, and records are now the training manual. Hand over the system, not the chaos." },
          { t: "Stay in the room", d: "Office hours stay open to graduates. Bring your scaling questions — hiring, pricing moves, second crews." },
        ].map((s, i) => (
          <div key={s.t} className="bg-charcoal p-6">
            <p className="font-mono text-[11px] text-safety">[0{i + 1}]</p>
            <p className="font-display font-bold text-white mt-2">{s.t}</p>
            <p className="text-slate-400 text-sm mt-2 leading-relaxed">{s.d}</p>
          </div>
        ))}
      </FadeUp>
    </div>
  );
}
